import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { alpha, keyframes, styled, type Theme } from "@mui/material/styles";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { hugsItsLabel } from "_shared/_views/buttons/action-button-width";
import {
  actionRowContainment,
  actionRowSeatsOneButton,
  actionRowTracks,
} from "_shared/_views/buttons/action-row-layout";
import LoadingButton from "_shared/_views/loading/loading-button";
import {
  paneIsCompact,
  paneIsComfortable,
  workPaneContainment,
  workPaneFullBleed,
  workPaneGutters,
  workPaneHeaderGutters,
} from "work/_views/work-pane-layout";

export const pulse = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.85;
  }
  50% {
    transform: scale(1.05);
    opacity: 1.0;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.85;
  }
`;

export const Header = styled("section")(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  paddingBottom: theme.spacing(1),
  position: "sticky",
  top: "0px",
  zIndex: 10,
}));

export const TabsContainer = styled(Tabs)(({ theme }) => ({
  "&.MuiTabs-root": {
    overflow: "hidden",
    "& .MuiTabs-scroller": {
      [paneIsCompact(theme)]: {
        paddingTop: 0,
      },
      paddingTop: theme.spacing(0.5),
    },
    "& .MuiTabs-scrollButtons.Mui-disabled": {
      opacity: "0.3",
    },
  },
}));

export const Content = styled("section")(({ theme }) => ({
  alignItems: "center",
  display: "contents",
  flex: 1,
  flexDirection: "row",
  height: "100%",
  minHeight: "25rem",
  width: "100%",
  [paneIsCompact(theme)]: {
    alignItems: "stretch",
    display: "flex",
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto",
  },
}));

export const TabContent = styled("section")(({ theme }) => ({
  alignItems: "stretch",
  flexDirection: "column",
  display: "flex",
  flex: 1,
  margin: theme.spacing(2, 0),
  minHeight: 0,
  overflow: "visible",
  width: "100%",
  [paneIsCompact(theme)]: {
    flex: "1 1 auto",
    margin: theme.spacing(1, 0, 0),
  },
}));

export const ActionsContainer = styled("section")(({ theme }) => ({
  [paneIsCompact(theme)]: {
    paddingBottom: theme.spacing(1),
    paddingTop: theme.spacing(1),
  },
  backgroundColor: theme.palette.background.default,
  bottom: "0px",
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  paddingBottom: theme.spacing(2.5),
  position: "sticky",
  width: "100%",
  zIndex: 10,
}));

export const IssueTrackerFooter = styled("section")(({ theme }) => ({
  alignItems: "center",
  backgroundColor: theme.palette.background.default,
  borderTop: `1px solid ${theme.palette.divider}`,
  bottom: 0,
  display: "flex",
  flexShrink: 0,
  justifyContent: "center",
  marginTop: "auto",
  paddingTop: theme.spacing(1),
  position: "sticky",
  width: "100%",
  zIndex: 15,
  [paneIsComfortable(theme)]: {
    display: "none",
  },
}));

export const IssueTrackerHeader = styled("section")(({ theme }) => ({
  display: "flex",
  [paneIsCompact(theme)]: {
    display: "none",
  },
}));

/**
 * The row's gap once the pane is compact, in pixels. It is the only gap the row can have where it
 * seats a single button, because a comfortable pane is always wide enough for two.
 */
const compactActionGap = (theme: Theme) => parseFloat(theme.spacing(0.5));

export const ProposalActions = styled("section")(({ theme }) => ({
  ...actionRowContainment,
  alignItems: "center",
  /**
   * On a comfortable pane each button is as wide as its label, side by side from the row's start
   * (#5973). The row used to share its whole width out among equal tracks, so four buttons ran from
   * one edge of the pane to the other, two disabled ones became wide grey slabs, and at 1280px a
   * fourth action fell onto a line of its own. Buttons that hug their labels leave the rest of the row
   * empty, and wrap only once the next one does not fit.
   */
  display: "flex",
  flex: "0 1 auto",
  flexWrap: "wrap",
  gap: theme.spacing(1),
  maxWidth: "100%",
  width: "100%",
  // Capping the wrapper a tooltip needs, not stretching it.
  "& > span": {
    maxWidth: "22rem",
  },
  // Below a comfortable pane the buttons wrap only when the next one will not fit (#5815). There is
  // no width below which the row is told to stack: it seats one button per row exactly when a second
  // one does not fit, which is what a 390px phone gets. Here the buttons do share the row out, so
  // the ones on a line are the same width.
  [paneIsCompact(theme)]: {
    alignContent: "end",
    display: "grid",
    flex: 1,
    gap: theme.spacing(0.5),
    gridTemplateColumns: actionRowTracks(theme),
    justifyContent: "start",
    width: "100%",
    "& > span": {
      maxWidth: "none",
    },
  },
}));

export const ReviewerBadge = styled(Badge)(() => ({
  marginLeft: "1rem",
}));

export const CommentButton = styled("section")(() => ({
  alignContent: "center",
  justifyContent: "flex-end",
}));

/**
 * The word under a proposal header icon, which names the panel that button opens.
 *
 * Kept on one line: the label is wider than the 40px button above it: "History" and "Comments" are
 * both wider than 40px at this size. A label that broke would take a second line
 * from the header row, which the proposal's name shares.
 */
export const CommentButtonLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>(({ disabled, theme }) => ({
  color: disabled ? theme.palette.action.disabled : undefined,
  fontSize: "0.6em",
  whiteSpace: "nowrap",
}));

export const HighlightCommentButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== "active" })<{
  active?: boolean;
}>(({ active }) => ({
  height: "40px",
  width: "40px",
  position: "relative",
  zIndex: 0,
  ...(active && {
    // Animate only decoration so the interactive hit target remains stable.
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(21, 101, 192, 0.05)",
      borderRadius: "50%",
      animation: `${pulse} 1.5s ease-in-out infinite`,
      zIndex: -1,
    },
  }),
}));

export const SaveButtonLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>(({ disabled, theme }) => ({
  color: disabled ? theme.palette.action.disabled : undefined,
  fontSize: "0.6em",
}));

export const SaveButton = styled(IconButton)(() => ({
  height: "40px",
  width: "40px",
  position: "relative",
  zIndex: 0,
}));

export const Fieldset = styled("span")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridColumnEnd: "span 2",
  gridTemplateColumns: "repeat(auto-fit, minmax(15rem, 1fr))",
  width: "100%",
  [paneIsCompact(theme)]: {
    display: "contents",
    gridColumnEnd: "auto",
  },
}));

export const ProposalActionButton = styled(LoadingButton)(({ theme }) => ({
  maxWidth: "22rem",
  // Wide enough that a one-word action ("Hire") is still a target, not so wide that a row of short
  // labels fills the pane again. See `ProposalActions`.
  minWidth: theme.spacing(15),
  whiteSpace: "nowrap",
  width: "auto",
  [paneIsCompact(theme)]: {
    maxWidth: "none",
    minWidth: 0,
    width: "100%",
    "& .MuiButton-icon": {
      flexShrink: 0,
    },
    "& .MuiButton-startIcon": {
      flexShrink: 0,
    },
    "& .MuiButton-endIcon": {
      flexShrink: 0,
    },
  },
  "&.Mui-disabled": {
    color: "rgba(0, 0, 0, 0.26) !important",
    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
  },
}));

/**
 * The way out of a negotiation, drawn to be found rather than pressed.
 *
 * Cancel Proposal and End Negotiation are one action seen from the two sides of a negotiation — the
 * person who opened it cancels, the person who received it ends — and both destroy work. Neither is
 * what a reader came to the page for, so neither takes a whole track of a row whose other buttons
 * carry the negotiation forward. Issue #5240 is the report that they did, and that Leave Team on a
 * work agreement is the shape they should have had.
 *
 * So they hug their label, and that is the whole of what sets them apart. They take the next track
 * in order, beside the buttons before them, and sit at its start. #5240 also pinned them to the
 * row's last column against its right edge, which put the row's buttons at opposite ends of the
 * pane with nothing between them. Review on #5815 asked for the buttons to stay next to each other.
 *
 * Where the row is too narrow for two buttons it is a single stacked column, and a full-width
 * button is what reads correctly there — so the hug is lifted. The lift asks the row rather than the
 * pane, so it lands at exactly the width the row stops seating two buttons (#5815).
 *
 * Both halves carry the alignment because only one of the two call sites has a span. Cancel Proposal
 * is disabled while a save is in flight and so needs the wrapper its tooltip hangs on, and the
 * wrapper is then the grid item; End Negotiation has no tooltip and is the grid item itself.
 */
export const ProposalQuietActionSpan = styled("span")(({ theme }) => ({
  [actionRowSeatsOneButton(theme, compactActionGap(theme))]: {
    justifySelf: "stretch",
  },
  justifySelf: "start",
}));

export const ProposalQuietActionButton = styled(ProposalActionButton)(({ theme }) => ({
  [actionRowSeatsOneButton(theme, compactActionGap(theme))]: {
    justifySelf: "stretch",
    minWidth: theme.spacing(24),
    width: "100%",
  },
  justifySelf: "start",
  ...hugsItsLabel,
}));

export const ProposalLoaderContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flex: 1,
  height: "100%",
  justifyContent: "center",
  minWidth: 0,
  width: "100%",
}));

export const ProposalPageContainer = styled(Box)(() => ({
  display: "flex",
  flexDirection: "row",
  height: "100%",
}));

/**
 * The page's content with the application footer under it, as one column beside the side panels'.
 * The footer used to span the whole row beneath both, so the panel column stopped above it: run past
 * it, a panel covered "Contact Us" (#4964); stopped short, the footer's band lay empty under the
 * Comments pane, 70px of white below the composer. Beside the panels, the footer is the content's
 * alone, and the panel column runs to the foot of the window.
 */
export const ProposalContentColumn = styled("div")(() => ({
  display: "flex",
  flex: 1,
  flexDirection: "column",
  minHeight: 0,
  minWidth: 0,
}));

/**
 * The column the page's comments and fee-schedule panels occupy, and the box they are drawn inside.
 *
 * Two jobs, which are the same job. It reserves the panel's width in the page's row so the content
 * lays itself out against what is left rather than against width the panel is about to cover; and it
 * gives the panel something to be positioned against, because Material draws a persistent drawer's
 * paper `fixed` to the window at the window's full height. Fixed to the window, the paper reached
 * past the bottom of the page area and over the application's footer — with comments open on a
 * 1280px display it covered "Contact Us" and took its clicks (#4964).
 *
 * The page area is bounded above by the app bar, so anchoring here also retires the `margin-top: 64px`
 * each of these panels carried to clear the app bar by hand. Below, it runs to the foot of the window:
 * the footer sits beside this column, at the foot of the content, not under it (`ProposalPageContent`).
 *
 * The selector reaches one level further than the panels' own `& .MuiDrawer-paper` rules so it wins
 * on specificity rather than on whichever stylesheet emotion happened to insert last.
 */
export const ProposalSidePanelColumn = styled("div")(() => ({
  /**
   * Nothing of the panel reaches outside the column the page reserved for it.
   *
   * A persistent drawer does not leave when it closes: it slides its paper out by its own width and
   * hides it. The paper is absolutely positioned here, so on a page that collapses this column to 0 it
   * went on contributing its 320px to the row's scrollable width — and on a phone that is a page which
   * scrolls 320px sideways onto nothing, landing there on load with the proposal pushed off the left
   * edge. Clipping is what the column already implies.
   *
   * `clip`, not `hidden`. A `hidden` box still scrolls when a script asks it to, and `scrollIntoView`
   * on anything in a panel asks every scrollable ancestor. A comment row did, on a "Prev / Next" stop,
   * and scrolled this column 34px sideways: the pane slid under the proposal, its left edge cut off,
   * and it stayed there after closing and opening again (#5821, #5824). A `clip` box is no scroll
   * container at all, so nothing in a panel can move the panel.
   */
  overflow: "clip",
  position: "relative",
  /**
   * Except while a panel's paper is wider than the column, which the fee schedule category editor's
   * paper is while its team member picker is up (#5798). The page keeps the column it reserved and
   * the paper lies over the page beside it; clipped, the picker's left half was cut off and the page
   * showed through, taking its clicks. The panel is open then, so the clipping's reason does not
   * apply, and the column clips again as soon as the paper is its own width.
   */
  "&:has(.MuiDrawer-paper[data-wide='true'])": {
    overflow: "visible",
  },
  /**
   * Nor while a fee schedule panel is open over a page that reserved no column for it. A phone's
   * column is 0px wide, so the open Add New Category or Select Existing Categories paper was clipped
   * to nothing: the page dimmed for a panel nobody could see. A closed panel's paper is 0px wide too,
   * so lifting the clip for an open one gives the closed ones no width to scroll onto.
   */
  "&:has(.MuiDrawer-paper[data-open='true'])": {
    overflow: "visible",
  },
  /**
   * A paper that lies over the page is no part of the column. The Comments pane is one where the
   * window has no room to seat it: a drawer over the page that is not modal (#6128), so it renders in
   * place, here. Placed in this column it was absolutely positioned inside 0px and clipped to nothing.
   * Fixed to the window, it is not clipped by the column at all.
   */
  "& .MuiDrawer-docked .MuiDrawer-paper:not([data-overlay='true'])": {
    height: "100%",
    marginTop: 0,
    position: "absolute",
    top: 0,
  },
}));

export const ProposalPageHeader = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  minWidth: 0,
  [paneIsCompact(theme)]: {
    gap: theme.spacing(1),
  },
}));

export const ProposalPageTitleContainer = styled(Box)(() => ({
  alignItems: "center",
  display: "flex",
  flex: 1,
  flexDirection: "row",
  minWidth: 0,
}));

export const ProposalPageActionButtons = styled(Box)(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexShrink: 0,
  gap: theme.spacing(2),
  [paneIsCompact(theme)]: {
    gap: theme.spacing(1),
  },
}));

export const ProposalPageTitle = styled(Typography)(({ theme }) => ({
  minWidth: 0,
  overflow: "hidden",
  padding: theme.spacing(0, 1),
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  [paneIsCompact(theme)]: {
    fontSize: "1.25rem",
    paddingLeft: theme.spacing(0.5),
  },
}));

export const ProposalPageBackButton = styled(IconButton)(({ theme }) => ({
  flexShrink: 0,
  height: 40,
  width: 40,
  [paneIsCompact(theme)]: {
    marginLeft: 0,
  },
}));

export const ProposalStickyPageHeader = styled("section")(({ theme }) => ({
  // The page's chrome stands in a gutter of its own rather than the content's — see `PaneBand`.
  // This outranks the pane's own gutter rather than adding to it, so the title, the tab strip and the
  // actions row below all measure from one number.
  ...workPaneHeaderGutters(theme),
  backgroundColor: theme.palette.background.default,
  display: "flex",
  flexDirection: "column",
  flexShrink: 0,
  position: "sticky",
  top: 0,
  zIndex: 20,
  "&::before": {
    backgroundColor: theme.palette.background.default,
    content: '""',
    height: theme.spacing(1),
    left: 0,
    pointerEvents: "none",
    position: "absolute",
    right: 0,
    top: theme.spacing(-1),
  },
  [paneIsCompact(theme)]: {
    "&::before": {
      height: theme.spacing(1),
      top: theme.spacing(-1),
    },
  },
}));

/**
 * The proposal's actions, under the title and above the tabs (#5973).
 *
 * They act on the whole proposal, as Save beside the title does, so they belong to the title. Drawn
 * between the tabs and the tab's content, they cut the tabs off from the panel they switch. The space
 * above is what sets them under the title rather than against it; below, the tabs follow closely.
 */
export const ProposalPageActionsContainer = styled(ActionsContainer)(({ theme }) => ({
  bottom: "auto",
  boxSizing: "border-box",
  flexShrink: 0,
  paddingBottom: theme.spacing(0.5),
  paddingTop: theme.spacing(1.5),
  position: "static",
  zIndex: "auto",
}));

/**
 * The handovers waiting on an answer, between the title and the actions (#6099).
 *
 * Inside the header, so they stand in its gutter and share the title's edge. They used to be a row
 * between the header and the tab with a margin of their own, which set them 24px further in than the
 * title and pushed the tab's template panel and divider down off the tab band's rule.
 */
export const ProposalPageNotices = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(1),
}));

/**
 * The tab strip that closes the proposal page's header, sitting on the content it switches.
 *
 * The rule under it separates the header from the page, so it runs the pane's full width and meets
 * the borders on either side rather than stopping inside the gutter. It sits inside the header, so the
 * gutter it has to climb out of is the header's. The indicator sits on the rule.
 */
export const ProposalTabsBand = styled(Header)(({ theme }) => ({
  ...workPaneFullBleed(theme, "header"),
  borderBottom: `1px solid ${theme.palette.divider}`,
  boxSizing: "border-box",
  minWidth: 0,
  overflow: "hidden",
  paddingBottom: 0,
}));

export const ProposalTabsHeader = styled(Header, {
  shouldForwardProp: (prop) => prop !== "compact",
})<{
  compact?: boolean;
}>(({ compact, theme }) => ({
  minWidth: 0,
  overflow: "hidden",
  [paneIsCompact(theme)]: {
    ...(compact && {
      paddingBottom: 0,
    }),
  },
}));

export const ProposalPageMain = styled("main", {
  shouldForwardProp: (prop) => prop !== "dimmed" && prop !== "mobilePadding" && prop !== "open",
})<{
  dimmed?: boolean;
  mobilePadding?: "compact" | "comfortable";
  open?: boolean;
}>(({ dimmed, mobilePadding = "comfortable", theme }) => ({
  /**
   * The pane names itself here, and everything drawn inside it lays out against this element's width
   * rather than the window's — see `work-pane-layout`. Its own rules are the one exception: a
   * container cannot query itself, which is why the horizontal gutter is declared for the pane's
   * children (`workPaneGutters`) and why the vertical padding below still asks the viewport.
   */
  ...workPaneContainment,
  ...workPaneGutters(theme),
  display: "flex",
  flex: 1,
  flexDirection: "column",
  height: "100%",
  marginRight: 0,
  minHeight: 0,
  overflowY: "auto",
  paddingTop: theme.spacing(1),
  position: "relative",
  transition: theme.transitions.create(["margin"], {
    duration: theme.transitions.duration.leavingScreen,
    easing: theme.transitions.easing.sharp,
  }),
  width: "100%",
  "&::after": {
    backgroundColor: alpha(theme.palette.background.default, 0.38),
    content: '""',
    inset: 0,
    opacity: dimmed ? 1 : 0,
    pointerEvents: "none",
    position: "absolute",
    transition: theme.transitions.create("opacity", {
      duration: dimmed ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
      easing: dimmed ? theme.transitions.easing.easeOut : theme.transitions.easing.sharp,
    }),
    zIndex: theme.zIndex.drawer - 1,
  },
  // Vertical only: the horizontal gutter is `workPaneGutters` above, which answers about the pane
  // at every width including this one. What is left here is the room a touch device wants above and
  // below the page, which is a question about the device and so still a question for the viewport.
  [theme.breakpoints.down("md")]: {
    padding: mobilePadding === "compact" ? 0 : theme.spacing(2, 0),
  },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginRight: 0,
        transition: theme.transitions.create(["margin"], {
          duration: theme.transitions.duration.enteringScreen,
          easing: theme.transitions.easing.easeOut,
        }),
      },
    },
  ],
}));

export const ProposalReviewerActionFieldset = styled(Fieldset)(() => ({
  gridColumnEnd: "auto",
  gridTemplateColumns: "minmax(0, 1fr)",
}));

/**
 * What a proposal pane says when it has nothing to list.
 *
 * A comment list with no comments, and any other pane with nothing in it, are the same kind of empty.
 * Drawn separately they drifted: the comments side was heading-sized and centered while the changes
 * side was a line of `body2` body copy, so the same "nothing here" arrived as two different kinds of
 * message (#4536). One component so they cannot part again.
 *
 * A pane is the surface this is sized for. A panel embedded beside a single field — the field-history
 * popper, a 300px card — says its "nothing here" in body copy instead; see ProposalTimelinePanel.
 */
export const ProposalEmptyPaneMessage = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  // The size the empty comment list has always been drawn at, and the treatment the app's empty tables
  // already use (see permissions' NoRowsText): larger than body copy, centered, given its own space.
  fontSize: "1.2rem",
  paddingTop: theme.spacing(2),
  textAlign: "center",
  // The comment list draws this inside a flex `ListItem`, where a content-width child cannot center.
  width: "100%",
}));
